import React, { useState, useEffect, useCallback } from "react";
import { Trash2, RotateCcw, AlertTriangle, Dumbbell, ClipboardList, FolderOpen } from "lucide-react";
import { toast } from "sonner";

const API = "/api";

const TrashManager = () => {
  const [trash, setTrash] = useState({ exercises: [], workouts: [], programs: [] });
  const [loading, setLoading] = useState(true);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/coach/trash`, { credentials: "include" });
      if (res.ok) setTrash(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const restore = async (type, id, name) => {
    const res = await fetch(`${API}/coach/trash/restore`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ type, id }),
    });
    if (res.ok) { toast.success(`"${name}" restored`); fetchTrash(); }
    else toast.error("Failed to restore");
  };

  const permDelete = async (type, id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    const res = await fetch(`${API}/coach/trash/permanent-delete`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ type, id }),
    });
    if (res.ok) { toast.success(`"${name}" permanently deleted`); fetchTrash(); }
    else toast.error("Failed to delete");
  };

  const totalItems = trash.exercises.length + trash.workouts.length + trash.programs.length;

  if (loading) return <div className="animate-pulse h-32 bg-[#141B2D] rounded-xl" />;

  const sections = [
    { key: "exercises", label: "Exercises", icon: Dumbbell, items: trash.exercises, idField: "exercise_id", nameField: "name", type: "exercise" },
    { key: "workouts", label: "Workouts", icon: ClipboardList, items: trash.workouts, idField: "workout_id", nameField: "title", type: "workout" },
    { key: "programs", label: "Programs", icon: FolderOpen, items: trash.programs, idField: "program_id", nameField: "title", type: "program" },
  ];

  return (
    <div className="space-y-6" data-testid="trash-manager">
      {totalItems === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Trash2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-semibold text-white/60">Trash is empty</p>
          <p className="text-sm mt-1">Deleted exercises, workouts, and programs will appear here.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{totalItems} item{totalItems !== 1 ? "s" : ""} in trash — restore or permanently delete</span>
          </div>
          {sections.map(sec => sec.items.length > 0 && (
            <div key={sec.key}>
              <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
                <sec.icon className="w-4 h-4 text-primary" /> {sec.label} ({sec.items.length})
              </h4>
              <div className="space-y-2">
                {sec.items.map(item => (
                  <div key={item[sec.idField]} className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-3 flex items-center justify-between" data-testid={`trash-${sec.type}-${item[sec.idField]}`}>
                    <div>
                      <p className="text-sm font-medium text-white">{item[sec.nameField]}</p>
                      {item.deleted_at && <p className="text-[10px] text-gray-500">Deleted {new Date(item.deleted_at).toLocaleDateString()}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => restore(sec.type, item[sec.idField], item[sec.nameField])}
                        className="flex items-center gap-1 bg-emerald-600/20 text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-600/30 transition-colors"
                        data-testid={`restore-${item[sec.idField]}`}>
                        <RotateCcw className="w-3 h-3" /> Restore
                      </button>
                      <button onClick={() => permDelete(sec.type, item[sec.idField], item[sec.nameField])}
                        className="flex items-center gap-1 bg-red-600/20 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-600/30 transition-colors"
                        data-testid={`perm-delete-${item[sec.idField]}`}>
                        <Trash2 className="w-3 h-3" /> Delete Forever
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default TrashManager;
